import { VOLUME_2022_2025 } from 'src/components/constants';
import {
  FilterSetAction,
  FilterType,
  filterSetActions,
} from './filterSetTypes';
import { FilterSetSelectionsActionTypes } from './actionTypes';

export interface AutocompleteOption {
  value: string;
  label: string;
}

export interface DateRange {
  gte: string;
  lte: string;
}

export interface FilterValue {
  type: FilterType | null;
  is_excluded: boolean;
  is_all: boolean;
  values: AutocompleteOption[];
}

export type ParentFilterType = 'FILTER';

export interface FilterSet {
  payerType: FilterValue;
  payerNames: FilterValue;
  placeOfService: FilterValue;
  siteOfCare: FilterValue;
  openPayments: FilterValue;
  specialties: FilterValue;
  siteSpecialties: FilterValue;
  hospitalSystem: FilterValue;
  gpoAffiliations: FilterValue;
  medicalSchools: FilterValue;
  residencies: FilterValue;
  fellowships: FilterValue;
  tags: FilterValue;
  providerLists: FilterValue;
  crmOwners: FilterValue;
  crmStatuses: FilterValue;
  inpatient: FilterValue;
  independent: FilterValue;
}

const FILTER_TYPE_MAPPING: Record<keyof FilterSet, FilterType> = {
  payerType: FilterType.PAYER_TYPE,
  payerNames: FilterType.PAYER_NAMES,
  placeOfService: FilterType.PLACE_OF_SERVICE,
  siteOfCare: FilterType.SOC_TYPE,
  openPayments: FilterType.OPEN_PAYMENTS,
  specialties: FilterType.SPECIALTY,
  siteSpecialties: FilterType.SITE_SPECIALTY,
  hospitalSystem: FilterType.HOSPITAL_SYSTEM,
  gpoAffiliations: FilterType.GPO_AFFILIATIONS,
  medicalSchools: FilterType.MEDICAL_SCHOOLS,
  residencies: FilterType.RESIDENCIES,
  fellowships: FilterType.FELLOWSHIPS,
  tags: FilterType.TAGS,
  providerLists: FilterType.PROVIDER_LISTS,
  crmOwners: FilterType.CRM_OWNERS,
  crmStatuses: FilterType.CRM_STATUSES,
  inpatient: FilterType.INPATIENT,
  independent: FilterType.INDEPENDENT,
};

export interface ParentFilterGroup {
  id: number;
  label: string;
  type: ParentFilterType;
  scope: 'PERSONAL' | 'COMPANY';
  owner: {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
  };
  filters: {
    id: number;
    filter_set: number;
    type: string;
    is_exclude: boolean;
    is_all: boolean;
    values: string[];
    created_at: string;
    updated_at: string;
  }[];
  detailed_filters: {
    id: number;
    filter_set: number;
    type: string;
    is_exclude: boolean;
    is_all: boolean;
    values: string[];
    created_at: string;
    updated_at: string;
    detailed_values: {
      value: string;
      label: string;
    }[];
  }[];
  created_at: string;
}

const defaultFilterValue: FilterValue = {
  type: null,
  is_excluded: false,
  is_all: false,
  values: [],
};

const defaultFilterSet: FilterSet = {
  payerType: { ...defaultFilterValue },
  payerNames: { ...defaultFilterValue },
  placeOfService: { ...defaultFilterValue },
  siteOfCare: { ...defaultFilterValue },
  openPayments: { ...defaultFilterValue },
  specialties: { ...defaultFilterValue },
  siteSpecialties: { ...defaultFilterValue },
  hospitalSystem: { ...defaultFilterValue },
  gpoAffiliations: { ...defaultFilterValue },
  medicalSchools: { ...defaultFilterValue },
  residencies: { ...defaultFilterValue },
  fellowships: { ...defaultFilterValue },
  tags: { ...defaultFilterValue },
  providerLists: { ...defaultFilterValue },
  crmOwners: { ...defaultFilterValue },
  crmStatuses: { ...defaultFilterValue },
  inpatient: { ...defaultFilterValue },
  independent: { ...defaultFilterValue },
};

const defaultDateRange: DateRange = {
  gte: VOLUME_2022_2025[0].value,
  lte: VOLUME_2022_2025[VOLUME_2022_2025.length - 1].value,
};

export interface FilterSetSelections {
  professional: number[];
  education: number[];
  salesEnablement: number[];
}

export type FilterSetState = {
  tempFilterSet: FilterSet;
  appliedFilterSet: FilterSet;
  tempDateRange: DateRange;
  appliedDateRange: DateRange;
  // table state
  columnId: string;
};

export const initialFilterSetState: FilterSetState = {
  tempFilterSet: defaultFilterSet,
  appliedFilterSet: defaultFilterSet,
  tempDateRange: defaultDateRange,
  appliedDateRange: defaultDateRange,

  columnId: null,
};

const validateFilterValue = (value: FilterValue): boolean => {
  if (!value || typeof value !== 'object') return false;
  if (typeof value.is_excluded !== 'boolean') return false;
  if (typeof value.is_all !== 'boolean') return false;
  if (!Array.isArray(value.values)) return false;
  return true;
};

export const filterSetReducer = (
  state: FilterSetState = initialFilterSetState,
  action: FilterSetAction
): FilterSetState => {
  try {
    switch (action.type) {
      case 'UPDATE_FILTER': {
        const { type, value } = action.payload;
        if (!validateFilterValue(value)) {
          console.error('Invalid filter value:', value);
          return state;
        }
        return {
          ...state,
          tempFilterSet: {
            ...state.tempFilterSet,
            [type]: {
              ...value,
              type: FILTER_TYPE_MAPPING[type],
            },
          },
        };
      }

      case 'REMOVE_FILTER_VALUE': {
        const { type, label } = action.payload;
        return {
          ...state,
          tempFilterSet: {
            ...state.tempFilterSet,
            [type]: {
              ...state.tempFilterSet[type],
              values: state.tempFilterSet[type].values.filter(
                (v) => v.label !== label
              ),
            },
          },
        };
      }

      case 'CLEAR_FILTER': {
        const { type } = action.payload;
        return {
          ...state,
          tempFilterSet: {
            ...state.tempFilterSet,
            [type]: { ...defaultFilterValue },
          },
        };
      }

      case 'CLEAR_ALL_FILTERS':
        return {
          ...state,
          tempFilterSet: defaultFilterSet,
        };

      case 'APPLY_FILTERS':
        return {
          ...state,
          appliedFilterSet: state.tempFilterSet,
        };

      case 'RESET_TO_APPLIED':
        return {
          ...state,
          tempFilterSet: state.appliedFilterSet,
        };

      case 'SET_TEMP_FILTER_SETS':
        return {
          ...state,
          tempFilterSet: action.payload.filterSets[0] || defaultFilterSet,
        };

      case 'SET_APPLIED_FILTER_SETS':
        return {
          ...state,
          appliedFilterSet: action.payload.filterSets[0] || defaultFilterSet,
        };

      case 'SET_TEMP_DATE_RANGE':
        return {
          ...state,
          tempDateRange: action.payload.dateRange,
        };

      case 'SET_APPLIED_DATE_RANGE':
        return {
          ...state,
          appliedDateRange: state.tempDateRange,
        };

      case 'SET_APPLIED_DATE_RANGE_DIRECT':
        return {
          ...state,
          appliedDateRange: action.payload.dateRange,
        };

      case FilterSetSelectionsActionTypes.SET_COLUMN_ID:
        return {
          ...state,
          columnId: action.payload.columnId || null,
        };

      default:
        return state;
    }
  } catch (error) {
    console.error('Filter set reducer error:', error);
    return state;
  }
};

export { filterSetActions };
